import * as jstp from '@infotech-soe/jstp';
import { JSTP_ERRORS } from '@infotech-soe/merps/types/errors';

import spawn from '../../framework/async/spawn';
import { EventDispatcher } from '../../framework/events/EventDispatcher';
import { InjectDebugLogger } from '../../framework/logging/decorators/InjectDebugLogger';
import { LogFormat } from '../../framework/logging/decorators/LogFormat';
import { LogMethod } from '../../framework/logging/decorators/LogMethod';
import store from '../../store';
import { isConnected } from '../../store/selectors/connection';

import { RemoteEvent } from './RemoteEvent';

/**
 * High-level JSTP connection wrapper.
 */
@InjectDebugLogger('jstp')
class JstpClient {
  /**
   * Proxies remote server events.
   */
  public onRemoteEvent = new EventDispatcher<RemoteEvent<any, any>>();
  public onReconnectAuthFailed = new EventDispatcher<void>();
  public onConnectionStatusChanged = new EventDispatcher<boolean>();

  /**
   * Raw JSTP connection object.
   */
  private reconnecting: boolean = false;
  private connection: jstp.Connection | null = null;
  private aController = new AbortController();

  /**
   * Establishes a connection to the remote endpoint.
   *
   * @param url - WebSocket URL of the remote party.
   * @param application - Name of the application to connect to.
   * @param options - connection options
   */
  @LogMethod
  public async connect(
    @LogFormat('%s') url: string,
    @LogFormat('%s') application: string,
    @LogFormat('%s') options?: Record<string, any>
  ): Promise<void> {
    if (this.hasConnection()) {
      throw new Error('Connection already established');
    }
    const [connection] = await spawn(
      jstp.wsBrowser,
      'connect',
      application,
      {
        heartbeatInterval: 8000,
        maxDumbHeartbeats: 2,
        reconnector: (
          c: jstp.Connection,
          reconnectFn: (e: any) => void
        ): void => {
          if (c.closedIntentionally || this.reconnecting) return;
          this.reconnecting = true;
          setTimeout(
            () => {
              reconnectFn((e: any) => {
                this.reconnecting = false;
                if (e?.code === JSTP_ERRORS.ERR_AUTH_FAILED) {
                  this.disposeConnection();
                  this.onReconnectAuthFailed.dispatch();
                }
              });
            },
            Math.floor(Math.random() * 4000) + 1000
          );
        },
        ...options,
      },
      url
    );
    this.setupConnection(connection);
    this.onConnectionStatusChanged.dispatch(true);
  }

  /**
   * Closes the underlying connection, if it exists. Otherwise, does nothing.
   */
  @LogMethod
  public disconnect(): void {
    if (this.hasConnection()) {
      this.disposeConnection();
    }
  }

  /**
   * Replaces the underlying raw JSTP connection object with a new one.
   *
   * @param connection - JSTP connection object.
   */
  @LogMethod
  public replaceConnection(connection: any): void {
    this.disconnect();
    this.setupConnection(connection);
  }

  /**
   * Makes an RPC call over JSTP.
   *
   * @param iface - Interface name.
   * @param method - Method name.
   * @param args - Method arguments.
   *
   * @returns Promise that resolves to RPC method result.
   *
   * If the method returns a single value, the promise resolves to the value
   * itself, i.e.,
   *
   *  ```
   *  rawConnection.callMethod(iface, method, args, (err, value) => {
   *    // ...
   *  })
   *  ```
   *
   *  becomes
   *
   *  ```
   *  client.callMethod(iface, method, args).then(value => {
   *    // ...
   *  }
   *  ```
   *
   *  If the method returns multiple values, the promise resolve to an array of
   *  these values, i.e.,
   *
   *  ```
   *  rawConnection.callMethod(iface, method, args, (err, value1, value2) => {
   *    // ...
   *  }
   *  ```
   *
   *  becomes
   *
   *  ```
   *  client.callMethod(iface, method, args).then(([value1, value2]) => {
   *    // ...
   *  }
   *  ```
   */
  @LogMethod
  public callMethod(
    @LogFormat('%s') iface: string,
    @LogFormat('%s') method: string,
    @LogFormat('%o') args: any[]
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.hasConnection()) {
        reject(new jstp.RemoteError(jstp.ERR_CONNECTION_LOST));
      }
      const abortListener = () => {
        this.aController = new AbortController();
        reject(new jstp.RemoteError(jstp.ERR_CONNECTION_LOST));
      };
      this.aController.signal.addEventListener('abort', abortListener, {
        once: true,
      });
      this.connection?.callMethod(
        iface,
        method,
        args,
        (error, ...result: any[]) => {
          this.aController.signal.removeEventListener('abort', abortListener);
          if (error) {
            reject(error);
          } else if (result.length <= 1) {
            // @ts-expect-error ts wants a tuple
            resolve(...result);
          } else {
            resolve(result);
          }
        }
      );
    });
  }

  private hasConnection(): boolean {
    return this.connection !== null && isConnected(store.getState());
  }

  private setupConnection(connection: any): void {
    this.connection = connection;
    this.setupConnectionEvents();
  }

  private setupConnectionEvents() {
    this.connection?.on(
      'event',
      (interfaceName: any, eventName: string, eventArgs: any[]) => {
        this.onRemoteEvent.dispatch(
          new RemoteEvent(interfaceName, eventName, eventArgs)
        );
      }
    );
    this.connection?.on('close', () => {
      this.onConnectionStatusChanged.dispatch(false);
    });
    this.connection?.on('reconnect', e => {
      if (!e) {
        this.onConnectionStatusChanged.dispatch(true);
      }
    });
  }

  private disposeConnection() {
    this.aController.abort();
    this.disposeConnectionEvents();
    this.connection?.close();
    this.connection = null;
    this.onConnectionStatusChanged.dispatch(false);
  }

  private disposeConnectionEvents() {
    this.connection?.removeAllListeners('event');
  }

  public get connectionId(): string | null {
    return this.connection?.session?.id || null;
  }
}

export default JstpClient;
